import { useQuery } from '@tanstack/react-query';
import { Timeline, Skeleton, EmptyState, DeviceSessionManager } from '@bbpos/ui';
import { userService } from '@/services/user.service';
import type { User } from '@/types/user';
import { buildUserActivityTimelineItems } from './user-activity-timeline';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';

const PANEL_CLASS =
  'rounded-2xl border border-slate-200/80 bg-white shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)]';

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

interface UserActivityTabProps {
  userId: string;
  user: User;
}

export function UserActivityTab({ userId, user }: UserActivityTabProps) {
  const { data: sessions, isLoading: sessionsLoading } = useQuery({
    queryKey: ['user', userId, 'sessions'],
    queryFn: () => userService.getSessions(userId),
    staleTime: 30_000,
  });

  const { data: shiftsData, isLoading: shiftsLoading } = useQuery({
    queryKey: ['user', userId, 'shifts'],
    queryFn: () => userService.getShifts(userId, { limit: 10 }),
    staleTime: 30_000,
  });

  const shifts = shiftsData?.data ?? [];

  const currency = useTenantCurrency();
  const items = buildUserActivityTimelineItems(user, shifts, currency);

  const isLoading = sessionsLoading || shiftsLoading;

  return (
    <div className="space-y-6">
      {/* Activity Timeline */}
      <div className={cn(PANEL_CLASS)}>
        <div className="p-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Recent Activity</h3>
              <p className="text-xs text-slate-500">Login sessions, shift history, and account events</p>
            </div>
          </div>
        </div>
        <div className="p-5">
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} variant="rect" height={48} />
              ))}
            </div>
          ) : items.length > 0 ? (
            <Timeline items={items} variant="default" maxItems={15} />
          ) : (
            <EmptyState
              variant="inline"
              title="No activity yet"
              description="Activity will appear here as the user logs in and works shifts"
              icon={
                <svg className="h-12 w-12 text-indigo-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={0.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
          )}
        </div>
      </div>

      {/* Active Sessions */}
      <div className={cn(PANEL_CLASS)}>
        <div className="p-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Active Sessions</h3>
              <p className="text-xs text-slate-500">Devices and browsers with active login sessions</p>
            </div>
          </div>
        </div>
        <div className="p-5">
          {sessionsLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} variant="rect" height={56} />
              ))}
            </div>
          ) : sessions && sessions.length > 0 ? (
            <DeviceSessionManager
              sessions={sessions}
              loading={sessionsLoading}
            />
          ) : (
            <EmptyState
              variant="inline"
              title="No active sessions"
              description="No active login sessions found for this user"
              icon={
                <svg className="h-12 w-12 text-emerald-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={0.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" />
                </svg>
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}
